import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../../shared/api/client'
import type {
  RosDashboardDto,
  RosDashboardFilters,
  RosDrillKind,
  RosDrillResult,
  RosPeriodKey,
} from './types'

export function useRosPeriod() {
  const [key, setKey] = useState<RosPeriodKey>('week')
  return { key, setKey }
}

export function useRosFilters() {
  const [filters, setFilters] = useState<RosDashboardFilters>({ type: '', city: '', buyerId: '' })
  const patch = (p: Partial<RosDashboardFilters>) => setFilters((f) => ({ ...f, ...p }))
  return { filters, patch }
}

async function fetchRosDashboard(
  period: RosPeriodKey,
  filters: RosDashboardFilters,
): Promise<RosDashboardDto> {
  const res = await apiClient.get<{ success: true; data: RosDashboardDto }>('/dashboard/ros', {
    params: {
      period,
      type: filters.type || undefined,
      city: filters.city || undefined,
      buyerId: filters.buyerId || undefined,
    },
  })
  return res.data.data
}

export function useRosDashboard(period: RosPeriodKey, filters: RosDashboardFilters) {
  return useQuery({
    queryKey: ['dashboard', 'ros', period, filters],
    queryFn: () => fetchRosDashboard(period, filters),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

/**
 * Списки позиций за период для модалок «Исход»/«Проквотировано» (16.07). Уходят
 * все фильтры дашборда — окно показывает ровно позиции плитки (29.09.2026).
 */
export function useRosDrilldown(
  kind: RosDrillKind | null,
  period: RosPeriodKey,
  filters: RosDashboardFilters,
) {
  return useQuery({
    queryKey: [
      'dashboard',
      'ros',
      'drilldown',
      kind,
      period,
      filters.type,
      filters.city,
      filters.buyerId,
    ],
    queryFn: async (): Promise<RosDrillResult> => {
      const res = await apiClient.get<{ success: true; data: RosDrillResult }>(
        '/dashboard/ros/drilldown',
        {
          params: {
            kind,
            period,
            type: filters.type || undefined,
            city: filters.city || undefined,
            buyerId: filters.buyerId || undefined,
          },
        },
      )
      return res.data.data
    },
    enabled: !!kind,
    staleTime: 30_000,
  })
}

/** Query-string для перехода «дашборд → очередь» с учётом активных фильтров. */
export function queueLink(
  params: Record<string, string | undefined>,
  filters?: RosDashboardFilters,
): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v)
  if (filters?.type && !params.requestType) qs.set('requestType', filters.type)
  if (filters?.buyerId && !params.buyerId) qs.set('buyerId', filters.buyerId)
  const s = qs.toString()
  return `/procurement/queue${s ? `?${s}` : ''}`
}
